import React, { useEffect, useRef, useState } from "react";
import { Button, Spin, Table, Tag, message } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  DollarOutlined,
  RiseOutlined,
  SwapOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { CustomButton, NextButton, PreviousButton } from "@comps/components";
import { makeRequest } from "@utils/helpers";
import { createSelectList } from "@comps/commonFunctions";
import { getRequestTypesReq, getRequestFlowReq } from "../requests";
import ChangeRequestFlow from "@mods/masterManagement/system/components/requestFlow/components/ChangeRequestFlow";
import ReadinessSummary from "./ReadinessSummary";

// Step 7 — Request Flow. Reuses the real approval-flow editor (ChangeRequestFlow):
// show all request types as a multi-select card grid, then configure the approval
// chain for each selected type in sequence (a guided queue). A summary table below
// shows every request type and its current approver chain. Optional/skippable.

const noop = () => {};

const iconFor = (typeId) => {
  const t = String(typeId || "");
  if (t.includes("leave")) return <CalendarOutlined />;
  if (t.includes("attendance")) return <ClockCircleOutlined />;
  if (t.includes("overtime")) return <FieldTimeOutlined />;
  if (t.includes("lend") || t.includes("loan") || t.includes("advance")) return <DollarOutlined />;
  if (t.includes("promotion")) return <RiseOutlined />;
  if (t.includes("transfer")) return <SwapOutlined />;
  if (t.includes("expense")) return <DollarOutlined />;
  return <FileTextOutlined />;
};

// ordered approver names for a flow's node list
const approverNames = (nodes) =>
  (nodes || [])
    .map((n) => n.request_as_name || n.employment_name)
    .filter(Boolean);

const buildCtx = (typeId, companyId) => ({
  company_id: companyId,
  type_id: typeId,
  for_node_id: null,
  for_employee_id: "",
  for_employment_id: "",
});

const StepRequestFlow = (props) => {
  const { wizard, patch, onNext, onBack, readiness, readinessLoading, refreshReadiness, statuses, skipStep, clearSkip } = props;
  const companyId = wizard.company?.id || readiness.company.record?.id || localStorage.getItem("company_id");

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [configured, setConfigured] = useState(new Set(wizard.requestflow?.configured || []));
  const [flowsByType, setFlowsByType] = useState({}); // type_id -> [nodes]

  // queue of type_ids to configure sequentially
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const queueRef = useRef([]);
  queueRef.current = queue;

  const [activeCtx, setActiveCtx] = useState(null);
  const [flowRecord, setFlowRecord] = useState(null);

  // load a single type's flow (for the summary table); silent loader to avoid flicker
  const loadFlow = (typeId) => {
    if (!companyId) return;
    makeRequest(
      noop,
      getRequestFlowReq,
      { request_flow: buildCtx(typeId, companyId) },
      (resp) => setFlowsByType((prev) => ({ ...prev, [typeId]: resp?.flow || [] })),
      null
    );
  };

  useEffect(() => {
    makeRequest(
      setLoading,
      getRequestTypesReq,
      "",
      (resp) => {
        const list = createSelectList(resp?.request_types || []);
        setTypes(list);
        list.forEach((t) => loadFlow(t.value));
      },
      () => setTypes([])
    );
    // eslint-disable-next-line
  }, []);

  const toggle = (typeId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  const openFor = (typeId) => {
    if (!companyId) {
      message.info("Create your company before configuring approval flows.");
      return;
    }
    const ctx = buildCtx(typeId, companyId);
    makeRequest(
      setLoading,
      getRequestFlowReq,
      { request_flow: ctx },
      (resp) => {
        setActiveCtx(ctx);
        setFlowRecord(resp);
      },
      (err, res) => {
        message.error(res?.response?.data?.message || "Could not load the approval flow");
        setQueue([]);
        setQIndex(0);
      }
    );
  };

  const startQueue = () => {
    const q = [...selected];
    if (!q.length) return;
    queueRef.current = q;
    setQueue(q);
    setQIndex(0);
    openFor(q[0]);
  };

  const onFlowDone = (res) => {
    const typeId = activeCtx?.type_id;
    setFlowRecord(null);
    setActiveCtx(null);

      if (res !== false && typeId) {
      const next = new Set(configured);
      next.add(typeId);
      setConfigured(next);
      patch({ requestflow: { configured: [...next] } });
      clearSkip?.("requestflow");
      loadFlow(typeId); // refresh the summary row for this type
      refreshReadiness?.();
    }

    const nextIndex = qIndex + 1;
    if (nextIndex < queueRef.current.length) {
      setQIndex(nextIndex);
      openFor(queueRef.current[nextIndex]);
    } else {
      message.success("Approval flows updated");
      setSelected(new Set());
      setQueue([]);
      setQIndex(0);
    }
  };

  const activeLabel =
    activeCtx && types.find((t) => t.value === activeCtx.type_id)?.label;

  const continueStep = () => {
    clearSkip?.("requestflow");
    onNext();
  };

  const skip = () => {
    skipStep?.("requestflow");
    message.info("Approval flows skipped for now.");
    onNext();
  };

  const summaryColumns = [
    {
      title: "Request Type",
      key: "type",
      render: (record) => (
        <span className="onb-reqflow-rowtype">
          <span className="onb-reqflow-rowicon">{iconFor(record.value)}</span>
          {record.label}
        </span>
      ),
    },
    {
      title: "Approval Chain (who approves)",
      key: "chain",
      render: (record) => {
        const names = approverNames(flowsByType[record.value]);
        if (!names.length) {
          return <span style={{ color: "#a79f92" }}>Not defined</span>;
        }
        return (
          <span>
            {names.map((nm, i) => (
              <React.Fragment key={`${record.value}-${i}`}>
                {i > 0 ? <span className="onb-reqflow-arrow">→</span> : null}
                <Tag color="orange">{nm}</Tag>
              </React.Fragment>
            ))}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div className="onb-card-title">Request approval flows</div>
      <div className="onb-card-sub">
        Optional: pick the request types you want to configure, then define who approves each step by step.
        This decides the approval chain employees follow when they submit a request.
      </div>

      <ReadinessSummary
        status={statuses.requestflow}
        loading={readinessLoading}
        onRefresh={refreshReadiness}
        items={[
          { label: "Request types", value: readiness.requestflow.typeCount || types.length || 0 },
          { label: "Flows configured", value: readiness.requestflow.configuredCount || configured.size || 0 },
        ]}
      />

      <Spin spinning={loading && !flowRecord}>
        <div className="onb-reqflow-grid">
          {types.map((t) => {
            const isSel = selected.has(t.value);
            return (
              <div
                key={t.value}
                className={`onb-reqflow-card ${isSel ? "selected" : ""}`}
                onClick={() => toggle(t.value)}
                role="button"
                tabIndex={0}
              >
                <span className="onb-reqflow-check">{isSel ? "✓" : ""}</span>
                <div className="onb-reqflow-ic">{iconFor(t.value)}</div>
                <div className="onb-reqflow-nm">{t.label}</div>
                {configured.has(t.value) ? (
                  <Tag color="green" className="onb-reqflow-tag">Flow set</Tag>
                ) : null}
              </div>
            );
          })}
          {!loading && !types.length ? (
            <div className="onb-card-sub">No request types found for this company.</div>
          ) : null}
        </div>
      </Spin>

      <div className="onb-reqflow-bar">
        <span className="cnt">{selected.size} selected</span>
        <CustomButton
          type="primary"
          disabled={!selected.size || !!flowRecord}
          onClick={startQueue}
          text="Define / Update Approval Flow"
        />
      </div>

      {activeCtx && queue.length ? (
        <div className="onb-reqflow-progress">
          Configuring {activeLabel || activeCtx.type_id} — {qIndex + 1} of {queue.length}
        </div>
      ) : null}

      <div className="onb-section-h">Current approval flows</div>
      <Table
        rowKey="value"
        size="small"
        columns={summaryColumns}
        dataSource={types}
        pagination={false}
        locale={{ emptyText: "No request types" }}
      />

      {flowRecord && activeCtx ? (
        <ChangeRequestFlow
          record={flowRecord}
          requestFlow={activeCtx}
          typeLabel={activeLabel}
          onCompleted={onFlowDone}
        />
      ) : null}

      <div className="onb-foot">
        <PreviousButton onClick={onBack} text="Back" />
        <span className="spacer" />
        <Button onClick={skip}>Skip for now</Button>
        <NextButton onClick={continueStep} text="Continue" />
      </div>
    </>
  );
};

export default StepRequestFlow;
